import { AutoComplete, Form } from "antd";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { SearchContext } from "./context";

interface LogisticsStagnantHoursFieldProps {}

const logisticsStagnantHoursOptions = [6, 12, 24, 36, 48, 72].map((hours) => ({
    value: String(hours),
    label: `超过${hours}小时`,
}));
const maxLogisticsStagnantHours = 72;

const getPositiveInteger = (value?: string): number | undefined => {
    const input = value?.trim();

    if (!input || !/^[1-9]\d*$/.test(input)) {
        return undefined;
    }

    const hours = Number(input);

    return Number.isSafeInteger(hours) && hours <= maxLogisticsStagnantHours ? hours : undefined;
};

const formatHours = (hours?: number) => (hours ? `超过${hours}小时` : undefined);

interface LogisticsStagnantHoursAutoCompleteProps {
    value?: number;
    onChange?: (value?: number) => void;
}

const LogisticsStagnantHoursAutoComplete: React.FC<LogisticsStagnantHoursAutoCompleteProps> = ({ value, onChange }) => {
    const { onPressEnter = () => {} } = React.useContext(SearchContext) || {};
    const [inputValue, setInputValue] = useState(() => formatHours(value));
    const [open, setOpen] = useState(false);
    const resetTimerRef = useRef<number>(undefined);

    useEffect(() => {
        if (!open || value === undefined) {
            setInputValue(formatHours(value));
        }
    }, [value, open]);

    useEffect(() => {
        return () => {
            if (resetTimerRef.current !== undefined) {
                window.clearTimeout(resetTimerRef.current);
            }
        };
    }, []);

    const resetInputValue = (nextValue?: string) => {
        if (resetTimerRef.current !== undefined) {
            window.clearTimeout(resetTimerRef.current);
        }

        setInputValue("");
        resetTimerRef.current = window.setTimeout(() => {
            setInputValue(nextValue);
            resetTimerRef.current = undefined;
        });
    };

    const handleChange = (nextInputValue: string) => {
        if (!nextInputValue) {
            setInputValue(undefined);
            onChange?.(undefined);
            return;
        }

        const hours = getPositiveInteger(nextInputValue);

        if (!hours) {
            resetInputValue(getPositiveInteger(inputValue) ? inputValue : value ? String(value) : undefined);
            return;
        }

        setInputValue(nextInputValue);
        onChange?.(hours);
    };

    const handleSelect = (selectedValue: string) => {
        const hours = getPositiveInteger(selectedValue);

        if (!hours) {
            return;
        }

        onChange?.(hours);
        setInputValue(formatHours(hours));
        setOpen(false);
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        /** 禁止antd的上下键索引 */
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        if (event.code === "NumpadEnter" || event.code === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            setInputValue(formatHours(getPositiveInteger(inputValue) ?? value));
            onPressEnter();

            return;
        }

        /** 不给复制粘贴进来 */
        if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
            return;
        }

        const rawInputValue = getPositiveInteger(inputValue) ? inputValue : value ? String(value) : "";

        if (!/^\d$/.test(event.key) || (event.key === "0" && !rawInputValue)) {
            event.preventDefault();
        }
    };

    return (
        <AutoComplete
            popupMatchSelectWidth={false}
            style={{ fontSize: 13, width: 110 }}
            className={classNames({
                "has-value": value,
            })}
            size="small"
            value={inputValue}
            open={open}
            allowClear
            placeholder="输入小时数"
            options={logisticsStagnantHoursOptions}
            onFocus={() => {
                setInputValue(value ? String(value) : undefined);
            }}
            onBlur={() => {
                setInputValue(formatHours(getPositiveInteger(inputValue) ?? value));
            }}
            onChange={handleChange}
            onSelect={handleSelect}
            onInputKeyDown={handleInputKeyDown}
            onOpenChange={setOpen}
        />
    );
};

const LogisticsStagnantHoursField: React.FC<LogisticsStagnantHoursFieldProps> = function () {
    return (
        <Form.Item name="logisticsStagnantHours" label={<span className="search-field-label">物流滞留</span>}>
            <LogisticsStagnantHoursAutoComplete />
        </Form.Item>
    );
};

export default React.memo(LogisticsStagnantHoursField);
